import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { GraphQlService } from '@shared/services/graphql-service';
import * as TerminalOperatorsActions from './terminal-operators.actions';
import {
  GET_TERMINAL_OPERATOR_BY_UUID,
  GET_TERMINAL_OPERATORS,
  UPDATE_TERMINAL_TANK,
} from '../graphql/terminal-operators.graphql';
import {
  GetTerminalOperatorResponse,
  ListTerminalOperatorsResponse,
} from '../responses/terminal-operators.response';

@Injectable({
  providedIn: 'root',
})
export class TerminalOperatorsEffects {
  private actions$ = inject(Actions);
  private graphQlService = inject(GraphQlService);

  $getTerminalOperators = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorsActions.TerminalOperatorsListActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<ListTerminalOperatorsResponse>({
            query: GET_TERMINAL_OPERATORS,
            variables: {
              pageableParam: request.pageableParam,
              statuses: request.statuses,
              wfa: request.wfa,
            },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.getAllTerminalRegistration;
              if (!payload) {
                return TerminalOperatorsActions.TerminalOperatorsListActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: 'Failed to fetch terminal operators',
                    data: null,
                  },
                });
              }

              return TerminalOperatorsActions.TerminalOperatorsListActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Success',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalOperatorsActions.TerminalOperatorsListActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to fetch terminal operators',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $getTerminalOperatorByUuid = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorsActions.TerminalOperatorByUuidActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetTerminalOperatorResponse>({
            query: GET_TERMINAL_OPERATOR_BY_UUID,
            variables: { terminalUuid: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult.data?.getTerminalRequestByUuid;
              if (!payload?.status || !payload?.data) {
                return TerminalOperatorsActions.TerminalOperatorByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to fetch terminal operator',
                    data: null,
                  },
                });
              }

              const details = payload.data;
              const activeTanks = (details?.tanks ?? []).filter(
                (tank: any) => tank.active !== false && tank.deleted !== true,
              );
              const normalizedDetails = {
                ...details,
                tanks: activeTanks,
                name: details.terminalName ?? null,
                registrationStatus: details.status ?? null,
                numberOfTanks: activeTanks.length,
              };

              return TerminalOperatorsActions.TerminalOperatorByUuidActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Success',
                  data: {
                    ...payload,
                    data: normalizedDetails,
                  },
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalOperatorsActions.TerminalOperatorByUuidActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to fetch terminal operator',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );

  $updateTerminalTank = createEffect(() =>
    this.actions$.pipe(
      ofType(TerminalOperatorsActions.UpdateTerminalTankActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .mutate<any>({
            mutation: UPDATE_TERMINAL_TANK,
            variables: { input: request },
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((apolloResult) => {
              const payload = apolloResult?.data?.updateTerminalTank;
              if (!payload?.status) {
                return TerminalOperatorsActions.UpdateTerminalTankActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: payload?.message ?? 'Failed to update terminal tank',
                    data: null,
                  },
                });
              }

              return TerminalOperatorsActions.UpdateTerminalTankActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: payload.message ?? 'Terminal tank updated successfully',
                  data: payload.data,
                },
              });
            }),
            catchError((error) =>
              of(
                TerminalOperatorsActions.UpdateTerminalTankActions.failure({
                  error: {
                    success: false,
                    statusCode: 400,
                    message: error?.message ?? 'Failed to update terminal tank',
                    data: null,
                  },
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
